export interface Lead {
  id: string;
  stage: LeadStage;
  status: LeadStatus;
  previous_stage?: string;
  previous_status?: string;
  last_stage_changed_at?: string;
  last_status_changed_at?: string;
  source: LeadSource;
  /** Website a Website/Form lead came from, e.g. servefar.com / propertyhandling.com */
  source_website?: string | null;
  property_type: string;
  name: string;
  email: string;
  phone: string;
  locality: string;
  city: string;
  state: string;
  amount?: string;
  service?: string;
  living_room?: string;
  bath_room?: string;
  follow_up_date: string;
  customer_location?: string;
  customer_city?: string;
  customer_state?: string;
  looking_for: string;
  maps_link?: string;
  created_at: string;
  updated_at?: string;
  created_by?: string;
  assigned_to?: string;
  assigned_user?: { id: string; name: string } | null;
  uploads?: LeadUpload[];
}

export interface LeadUpload {
  id: string;
  upload_key: string;
  original_name: string;
  mime_type: string;
  size?: number;
  created_at?: string;
}

export interface LeadListResponse {
  success: boolean;
  message: string;
  data: {
    leads: Lead[];
    total: number;
    pageCount: number;
  };
}

export interface LeadDetailResponse {
  success: boolean;
  message: string;
  data: Lead;
}

export interface LeadComment {
  id: string;
  comment: string;
  created_at: string;
  created_by: string;
}

export interface LeadCommentsResponse {
  success: boolean;
  message: string;
  data: LeadComment[];
}

export interface LeadHistory {
  id: string;
  field: 'stage' | 'status';
  previous_value: string | null;
  new_value: string;
  changed_at: string;
  changed_by: string | null;
}

export interface LeadHistoryResponse {
  success: boolean;
  message: string;
  data: LeadHistory[];
}

export interface LeadFormData {
  stage?: LeadStage;
  status?: LeadStatus;
  service?: string;
  source: LeadSource;
  property_type: string;
  name: string;
  email: string;
  phone: string;
  locality: string;
  city: string;
  state: string;
  follow_up_date: string;
  customer_location?: string;
  customer_city?: string;
  customer_state?: string;
  looking_for: string;
  assigned_to?: string;
  maps_link?: string;
}

export enum LeadStage {
  NEW = 'New',
  REACHED_OUT = 'Reached Out',
  INTRODUCTION_DONE = 'Introduction Done',
  INSPECTION_DONE = 'Inspection Done',
  DRAFT_AGREEMENT_SENT = 'Draft Agreement Sent',
  FINAL_AGREEMENT_SENT = 'Final Agreement Sent',
  TOKEN_FEE_PAID = 'Token/Fee Paid',
}

export enum LeadStatus {
  HOT = 'Hot',
  WARM = 'Warm',
  COLD = 'Cold',
  REJECT = 'Reject',
  CONVERTED = 'Converted',
}

export enum LeadSource {
  WEBSITE = 'Website',
  CALL = 'Call',
  WHATSAPP = 'Whatsapp',
  EMAIL = 'Email',
  EPOQUE = 'Epoque(Lead Generator)',
  OTHERS = 'Others'
}

/**
 * Badge class for a lead's source. Website/Form leads are coloured by the
 * website they came from so servefar.com and propertyhandling.com leads
 * are easy to tell apart.
 */
export function leadSourceClass(lead: Pick<Lead, 'source' | 'source_website'>): string {
  const website = (lead.source_website || '').toLowerCase();
  if (website.includes('servefar')) return 'lead-source-servefar';
  if (website.includes('propertyhandling')) return 'lead-source-propertyhandling';
  if (website) return 'bg-info';
  return (lead.source as string) === 'Website/Form' ? 'bg-primary' : 'bg-light text-dark';
}

/** Text colour for the website shown under a lead's source badge. */
export function leadSourceWebsiteClass(website?: string | null): string {
  const site = (website || '').toLowerCase();
  if (site.includes('servefar')) return 'lead-site-servefar';
  if (site.includes('propertyhandling')) return 'lead-site-propertyhandling';
  return 'text-muted';
}

export const LEAD_SERVICE_OPTIONS = [
  'Tenant Placement',
  'Property Management',
  'Sales',
  'Plot & Land Monitoring',
].map(s => ({ value: s, label: s }));

export const LEAD_STAGE_OPTIONS = Object.values(LeadStage).map(stage => ({
  value: stage,
  label: stage
}));

export const LEAD_STATUS_OPTIONS = Object.values(LeadStatus).map(status => ({
  value: status,
  label: status
}));
